import React, { useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import getDataFromApi from "../../../components/getDataFromApi";
import "./History.scss";
import Sidenav from "../../navbar/Sidenav";
import TitleHeader from "../../project/header/TitleHeader";
import {
  BackIcon,
  HistoryTimeIcon,
} from "../../Common/SvgIcons/WorkflowIcons";
import PathBreadcrumb from "../../../components/PathBreadcrumb/PathBreadcrumb";
import { useLocation, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import HistorySubRow from "./HistorySubRow";
import EvaluationAnalytics from "../../hub/EvaluationAnalytics";

const getInitials = (name = "") => {
  return name
    .split(" ")
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
};

const formatHeaderLabel = (key) => {
  return key
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

export default function History() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedTenantId, setExpandedTenantId] = useState(null);
  const [search, setSearch] = useState("");
  const [showAnalytics, setShowAnalytics] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const viewFrom = location.state?.viewFrom;
  const isOwnProfile = Boolean(location.state?.isOwnProfile);
  const role = localStorage.getItem("role");


  const isTenantView = (viewFrom === "Tenant" || viewFrom === "User");
  const isTenantAdminView = role === "tenant_admin" && viewFrom === "Tenant";

  const tenantIdFromStorage = useMemo(() => {
    if (!isTenantView) return null;

    try {
      return localStorage.getItem("tenant_id") || "";
    } catch (error) {
      console.error("Failed to read tenant_id from localStorage:", error);
      return "";
    }
  }, [isTenantView]);

  useEffect(() => {
    if (isTenantView) {
      setLoading(false);
      return;
    }

    setLoading(true);

    const filters = {
      level: role,
      table_type: "main_table",
    };

    getDataFromApi(
      `${window.ENV.API_URL}/models?filters=${encodeURIComponent(
        JSON.stringify(filters)
      )}&section=evaluation_stats`,
      "get"
    )
      .then((r) => {
        if (!r || typeof r !== "object") {
          setData([]);
          return;
        }

        const transformed = Object.entries(r).map(([tenantId, value]) => ({
          ...value,
          tenantId,
        }));

        setData(transformed);

        // if (transformed.length > 0) {
        //   setExpandedTenantId(transformed[0].tenantId);
        // }
      })
      .catch((error) => {
        console.error("Failed to fetch evaluation stats:", error);
        setData([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [isTenantView]);

  const orderedFields = useMemo(() => {
    if (!data.length) return [];

    const fieldOrder = data[0]?.field_order || {};

    return Object.entries(fieldOrder)
      .sort(([, a], [, b]) => a - b)
      .map(([key]) => key);
  }, [data]);

  const filteredData = useMemo(() => {
    let rows = [...data];

    if (search.trim()) {
      const q = search.toLowerCase();

      rows = rows.filter((tenant) =>
        (tenant.tenant_name || "").toLowerCase().includes(q)
      );
    }

    return rows;
  }, [data, search]);


  const historyPathItems = (() => {
    if (isOwnProfile || viewFrom === "Profile") {
      return [
        {
          label: "Profile",
          onClick: () =>
            navigate("/user-profile", {
              state: {
                id: localStorage.getItem("user_id"),
                viewFrom: "profile_dashboard",
              },
            }),
        },
        { label: "History" },
      ];
    }
    if (viewFrom === "Admin") {
      return [
        { label: "Admin", onClick: () => navigate("/admin") },
        { label: "History" },
      ];
    }
    if (viewFrom === "Tenant") {
      // Tenant admin viewing their own dashboard history
      if (role === "tenant_admin") {
        return [
          { label: "Administration", onClick: () => navigate("/admin") },
          { label: "History" },
        ];
      }
      // System admin came from a tenant details screen
      return [
        { label: "Admin", onClick: () => navigate("/admin") },
        {
          label: "Tenant",
          onClick: () => navigate(-1),
        },
        { label: "History" },
      ];
    }
    if (viewFrom === "User") {
      return [
        { label: "Admin", onClick: () => navigate("/admin") },
        {
          label: "User",
          onClick: () => navigate(-1),
        },
        { label: "History" },
      ];
    }
    return [
      { label: "Admin", onClick: () => navigate("/admin") },
      { label: "History" },
    ];
  })();

  return (
    <>

      <div className="mainmenu projects-content de-width">
        <PathBreadcrumb
          onBack={() => {
            if (isOwnProfile || viewFrom === "Profile") {
              navigate("/user-profile", {
                state: {
                  id: localStorage.getItem("user_id"),
                  viewFrom: "profile_dashboard",
                },
              });
              return;
            }
            if (viewFrom === "Admin" || (viewFrom === "Tenant" && role === "tenant_admin")) {
              navigate("/admin");
              return;
            }
            navigate(-1);
          }}
          items={historyPathItems}
        />

        <div className="d-a justify-content-between gap-3">
          <TitleHeader
            Icon={HistoryTimeIcon}
            title1={"Audit"}
            title2={" History"}
            subtext={`Centralized audit trail for tenant model evaluations, device usage, and execution performance.`}
          />
        </div>

        {isTenantAdminView && (
          <div className="container-card history-analytics-banner mt-4">
            <div className="history-analytics-banner__copy">
              <span className="history-analytics-banner__dot" />
              <span>
                <strong className="text-primary">
                  {showAnalytics
                    ? "Evaluation Analytics:"
                    : "Evaluation Analytics:"}
                </strong>{" "}
                <small className="fw-light">
                  View evaluation activity across all models, chipsets, devices, and users.
                </small>
              </span>
            </div>
            <button
              type="button"
              className="plain-button2 text-primary fw-bold text-nowrap"
              onClick={() => setShowAnalytics((current) => !current)}
              aria-pressed={showAnalytics}
            >
              {showAnalytics ? (
                <>
                  Back to Audit History{" "}
                  <BackIcon className="wf-icon-small text-primary" />
                </>
              ) : (
                <>
                  View Analytics{" "}
                  <BackIcon
                    className="wf-icon-small text-primary"
                    style={{ transform: "rotate(141deg)" }}
                  />
                </>
              )}
            </button>
          </div>
        )}

        {isTenantView ? (
          isTenantAdminView && showAnalytics ? (
            <div className="mt-4">
              <EvaluationAnalytics />
            </div>
          ) : (
            <div className={isTenantAdminView ? "mt-3" : "mt-4"}>
              <HistorySubRow tenantId={tenantIdFromStorage} viewFrom={viewFrom} />
            </div>
          )
        ) : (
          <>
            <div className="d-a gap-2 p-2 badge-border px-3 flex-fill sub-nav-mb-button w-100 mt-4">
              <FontAwesomeIcon
                className="plain-button2 text-secondary"
                icon={faSearch}
              />
              <input
                className="border-0 m-0 bg-transparent text w-100"
                style={{ outline: "none" }}
                placeholder="Search tenant..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <div className="table-wrap mt-4">
              <div className="history-desktop-view">
                <table className="tenant-table container-card bg-transparent p-0">
                  <thead>
                    <tr>
                      {orderedFields.map((field, index) => (
                        <th
                          key={field}
                          className={
                            index === 0
                              ? "th-left"
                              : index === orderedFields.length - 1
                                ? "th-center"
                                : "text-center"
                          }
                        >
                          {formatHeaderLabel(field)}
                        </th>
                      ))}
                      <th className="th-right">Actions</th>
                    </tr>
                  </thead>

                  <tbody>
                    {loading ? (
                      <tr>
                        <td
                          colSpan={orderedFields.length + 1}
                          className="text-center py-4 history-empty"
                        >
                          Loading...
                        </td>
                      </tr>
                    ) : filteredData.length === 0 ? (
                      <tr>
                        <td
                          colSpan={orderedFields.length + 1}
                          className="text-center py-4 history-empty"
                        >
                          No evaluation stats found.
                        </td>
                      </tr>
                    ) : (
                      filteredData.map((tenant) => {
                        const isExpanded = expandedTenantId === tenant.tenantId;

                        return (
                          <React.Fragment key={tenant.tenantId}>
                            <tr className="tenant-main-row">
                              {orderedFields.map((field, index) => (
                                <td
                                  key={field}
                                  className={
                                    index === 0
                                      ? "p-0 ps-3 text-left"
                                      : "p-0 text-center"
                                  }
                                >
                                  {field === "tenant_name" ? (
                                    <div className="d-flex align-items-center gap-2">
                                      <div className="history-avatar">
                                        {getInitials(tenant[field])}
                                      </div>
                                      <div className="history-tenant-name">
                                        {tenant[field]}
                                      </div>
                                    </div>
                                  ) : (
                                    tenant[field]
                                  )}
                                </td>
                              ))}

                              <td className="text-right p-0 pe-3">
                                <button
                                  type="button"
                                  className="btn history-expand-btn"
                                  onClick={() =>
                                    setExpandedTenantId(
                                      isExpanded ? null : tenant.tenantId
                                    )
                                  }
                                >
                                  {isExpanded ? (
                                    <ChevronDown size={16} />
                                  ) : (
                                    <ChevronRight size={16} />
                                  )}
                                </button>
                              </td>
                            </tr>

                            {isExpanded && (
                              <tr className="tenant-details-row">
                                <td
                                  colSpan={orderedFields.length + 1}
                                  className="px-3 py-2"
                                >
                                  <HistorySubRow
                                    tenantId={tenant.tenant_id || tenant.tenantId}
                                    viewFrom={viewFrom}
                                  />
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              <div className="history-mobile-view">
                {loading ? (
                  <div className="history-empty text-center py-4">Loading...</div>
                ) : filteredData.length === 0 ? (
                  <div className="history-empty text-center py-4">
                    No evaluation stats found.
                  </div>
                ) : (
                  filteredData.map((tenant) => {
                    const isExpanded = expandedTenantId === tenant.tenantId;

                    return (
                      <div
                        className="history-mobile-card container-card mb-3"
                        key={tenant.tenantId}
                      >
                        <div
                          className="d-a justify-content-between"
                          onClick={() =>
                            setExpandedTenantId(
                              isExpanded ? null : tenant.tenantId
                            )
                          }
                        >
                          <div className="d-flex align-items-center gap-2">
                            <div className="history-avatar">
                              {getInitials(tenant.tenant_name)}
                            </div>
                            <div>
                              <h6 className="m-0">{tenant.tenant_name}</h6>
                            </div>
                          </div>

                          <button type="button" className="btn history-expand-btn">
                            {isExpanded ? (
                              <ChevronDown size={18} />
                            ) : (
                              <ChevronRight size={18} />
                            )}
                          </button>
                        </div>

                        <div className="d-a gap-3 flex-wrap">
                          {orderedFields
                            .filter((field) => field !== "tenant_name")
                            .map((field) => (
                              <div className="container-card-right flex-fill" key={field}>
                                <div className="history-mobile-stat-label">
                                  {formatHeaderLabel(field)}
                                </div>
                                <div className="fw-bold history-mobile-stat-value">
                                  {tenant[field] ?? 0}
                                </div>
                              </div>
                            ))}
                        </div>

                        {isExpanded && (
                          <div className="history-mobile-subrow">
                            <HistorySubRow
                              tenantId={tenant.tenant_id || tenant.tenantId}
                              viewFrom={viewFrom}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}
